import React, {
    createContext,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import {BalloonEventModel, BeatAPIBalloonEventsRepository, BeatAPIUserRepository, GuestBookmarkRepository, UserModel} from 'ts-core';
import {useApi} from './Api';
import {useBalloonEventsMetadata} from './Metadata';
import {AuthModal} from '../components/AuthForm';
import {identifyUser, resetUser, trackMixpanel} from '../utils/mixpanel';

export interface UserInfo {
    loading: boolean;
    user: UserModel | null | undefined;
    userRepositoryRef: React.MutableRefObject<BeatAPIUserRepository | null>;
    login: ((email: string, password: string) => Promise<UserModel>) | undefined;
    loginWithOAuth: ((provider: string) => Promise<UserModel>) | undefined;
    logout: (() => Promise<void>) | undefined;
    deleteUser: (() => Promise<void>) | undefined;
    register:
        | ((email: string, password: string, name: string, language?: string) => Promise<void>)
        | undefined;
    loadUser: (() => Promise<void>) | undefined;
    toggleBookmark: ((event: BalloonEventModel) => Promise<void>) | undefined;
    googleLogin: ((token: string) => Promise<UserModel>) | undefined;
    discordLogin: ((token: string) => Promise<UserModel>) | undefined;
    changePassword:
        | ((currentPassword: string | null, newPassword: string) => Promise<void>)
        | undefined;
    uploadAvatar: ((file: File) => Promise<void>) | undefined;
    deleteAvatar: (() => Promise<void>) | undefined;
    authDialogIsOpen: boolean;
    setAuthDialogIsOpen: (isOpen: boolean) => void;
    authRedirectAfter?: string;
    requiresUser: (options: RequiresUserOptions) => void;
    setAuthRedirectAfter: (redirectAfter: string | undefined) => void;
    /**
     * Full event models for all bookmarked items. Unified across authenticated
     * (server) and guest (localStorage) bookmarks — guest IDs are fetched via
     * the balloon events repository so consumers like BookmarksPage render the
     * same way regardless of auth state.
     */
    bookmarks: BalloonEventModel[];
    /**
     * All bookmarked balloon IDs. Unified across authenticated (server) and
     * guest (localStorage) bookmarks so callers (e.g. BookmarkButton) can ask
     * "is this event bookmarked?" without caring which backing store is active.
     */
    bookmarkedIds: string[];
    loadBookmarks: () => Promise<void>;
}

export interface RequiresUserOptions {
    callback: Function;
}

export const UserContext = createContext<UserInfo | undefined>(undefined);

export type UserProviderProps = {
    children: ReactNode;
    partnerKey: string;
};

export const UserProvider: React.FC<UserProviderProps> = ({
                                                              children,
                                                              partnerKey,
                                                          }) => {
    const {apiEndpoints, apiKey = ''} = useApi();
    const userRepository = useRef<BeatAPIUserRepository | null>(null);
    const eventsRepository = useRef<BeatAPIBalloonEventsRepository | null>(null);
    const {metadata} = useBalloonEventsMetadata();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<UserModel | null | undefined>(undefined);
    const [authDialogIsOpen, setAuthDialogIsOpen] = useState(false);
    const [authRedirectAfter, setAuthRedirectAfter] = useState<
        string | undefined
    >(undefined);
    const [onLogin, setOnLogin] = useState<Function | undefined>(undefined);
    const [guestBookmarkIds, setGuestBookmarkIds] = useState<string[]>(() =>
        GuestBookmarkRepository.load().map((b) => b.id)
    );
    const [guestBookmarkEvents, setGuestBookmarkEvents] = useState<BalloonEventModel[]>([]);

    // Keep guest bookmark state in sync with localStorage (multi-tab).
    useEffect(() => {
        return GuestBookmarkRepository.subscribe(() => {
            setGuestBookmarkIds(GuestBookmarkRepository.load().map((b) => b.id));
        });
    }, []);

    // Fetch full event models for guest-bookmarked IDs so consumers like
    // BookmarksPage can render the same card grid regardless of auth state.
    // Skipped when the user is logged in — authedBookmarks are already on the
    // user model, and the login flow merges guest items via bulkImportBookmarks.
    useEffect(() => {
        if (user) {
            setGuestBookmarkEvents([]);
            return;
        }
        if (!eventsRepository.current || guestBookmarkIds.length === 0) {
            setGuestBookmarkEvents([]);
            return;
        }
        let cancelled = false;
        eventsRepository.current.loadAll(guestBookmarkIds).then((events) => {
            if (!cancelled) setGuestBookmarkEvents(events);
        });
        return () => {
            cancelled = true;
        };
    }, [user, guestBookmarkIds]);

    async function mergeGuestBookmarksIntoAccount(): Promise<void> {
        const guestItems = GuestBookmarkRepository.load();
        if (!guestItems.length || !userRepository.current) return;
        try {
            const result = await userRepository.current.bulkImportBookmarks(
                guestItems.map((i) => i.id)
            );
            if (result) {
                GuestBookmarkRepository.clear();
                setGuestBookmarkIds([]);
                trackMixpanel('Bookmark', 'GuestMerge:Success', {
                    added: result.added,
                    skipped: result.skipped,
                });
            }
        } catch (e) {
            // Keep localStorage intact; next login will retry.
            trackMixpanel('Bookmark', 'GuestMerge:Failed');
        }
    }

    function handleUserChange(user: UserModel | null) {
        setUser(user);
        if (user) {
            localStorage.setItem('token', user.token);
            identifyUser(user.id ?? user.token, {
                name: user.name ?? '',
                email: user.email ?? '',
            });
            // Silently merge guest bookmarks, then refresh the server list.
            // Don't block the login UX on this.
            mergeGuestBookmarksIntoAccount()
                .then(() => loadBookmarks())
                .catch(() => undefined);
            if (onLogin) {
                onLogin();
                setOnLogin(undefined);
                setAuthDialogIsOpen(false);
            }
        } else {
            localStorage.removeItem('token');
        }
    }

    useEffect(() => {
        if (metadata == null) return;

        userRepository.current = new BeatAPIUserRepository(
            metadata,
            apiEndpoints,
            partnerKey
        );
        eventsRepository.current = new BeatAPIBalloonEventsRepository(
            metadata,
            apiEndpoints,
            apiKey,
        );

        // Auto-load user from token if exists
        const token = localStorage.getItem('token');
        if (token) {
            const user = new UserModel(null, token);
            userRepository.current.user = user;
            // Try to load user, but if token is invalid (401), clear it
            loadUser().catch((error) => {
                console.warn(
                    'Failed to load user with stored token, clearing token:',
                    error
                );
                localStorage.removeItem('token');
                if (userRepository.current) {
                    userRepository.current.user = null;
                }
                setUser(null);
                setLoading(false);
            });
            return;
        }

        setLoading(false);
    }, [metadata, apiEndpoints, apiKey, partnerKey]);

    async function login(email: string, password: string): Promise<UserModel> {
        try {
            setLoading(true);
            await userRepository.current!.login({email, password});
            await loadUser();
            handleUserChange(userRepository.current!.user);
            trackMixpanel('Auth', 'LoginSuccess');
            setLoading(false);
            return userRepository.current!.user!;
        } catch (e) {
            trackMixpanel('Auth', 'LoginFailed');
            setLoading(false);
            throw e;
        }
    }

    async function loginWithOAuth(provider: string): Promise<UserModel> {
        try {
            setLoading(true);
            await userRepository.current!.handleOAuthCallback(provider);
            handleUserChange(userRepository.current!.user);
            trackMixpanel('Auth', 'OAuthSuccess', {provider});
            setLoading(false);
            return userRepository.current!.user!;
        } catch (e) {
            trackMixpanel('Auth', 'OAuthFailed', {provider});
            console.error('OAuth login failed:', e);
            setLoading(false);
            throw e;
        }
    }

    async function register(
        email: string,
        password: string,
        name: string,
        language?: string
    ): Promise<void> {
        try {
            setLoading(true);
            await userRepository.current!.register({email, password, name, language});
            trackMixpanel('Auth', 'RegisterSuccess');
            // Don't load user or log in - user must verify email first
            setLoading(false);
        } catch (e) {
            trackMixpanel('Auth', 'RegisterFailed');
            setLoading(false);
            throw e;
        }
    }

    async function googleLogin(token: string): Promise<UserModel> {
        try {
            setLoading(true);
            await userRepository.current!.googleLogin(token);
            await loadUser();
            handleUserChange(userRepository.current!.user);
            trackMixpanel('Auth', 'OAuthSuccess', {provider: 'google'});
            setLoading(false);
            return userRepository.current!.user!;
        } catch (e) {
            trackMixpanel('Auth', 'OAuthFailed', {provider: 'google'});
            setLoading(false);
            throw e;
        }
    }

    async function discordLogin(token: string): Promise<UserModel> {
        try {
            setLoading(true);
            await userRepository.current!.discordLogin(token);
            await loadUser();
            handleUserChange(userRepository.current!.user);
            trackMixpanel('Auth', 'OAuthSuccess', {provider: 'discord'});
            setLoading(false);
            return userRepository.current!.user!;
        } catch (e) {
            trackMixpanel('Auth', 'OAuthFailed', {provider: 'discord'});
            setLoading(false);
            throw e;
        }
    }

    async function logout(): Promise<void> {
        setLoading(true);
        trackMixpanel('Auth', 'Logout');
        await userRepository.current!.logout();
        resetUser();
        handleUserChange(null);
        setLoading(false);
    }

    async function loadUser(): Promise<void> {
        setLoading(true);
        await userRepository.current!.loadUser();
        // Create new instance to trigger React re-renders
        const updatedUser = userRepository.current!.user ? UserModel.from(userRepository.current!.user) : null;
        setUser(updatedUser);
        setLoading(false);
    }

    async function toggleBookmark(event: BalloonEventModel): Promise<void> {
        // Guest path: store locally, no auth required.
        if (!user) {
            if (GuestBookmarkRepository.has(event.id)) {
                GuestBookmarkRepository.remove(event.id);
            } else {
                GuestBookmarkRepository.add({
                    id: event.id,
                    slug: event.slug,
                    title: event.title,
                });
            }
            // subscribe() handler updates guestBookmarkIds state.
            return;
        }
        await userRepository.current!.toggleBookmark(event);
        // Create new instance to trigger React re-renders
        const updatedUser = userRepository.current!.user ? UserModel.from(userRepository.current!.user) : null;
        setUser(updatedUser);
    }

    async function loadBookmarks(): Promise<void> {
        if (!userRepository.current || !userRepository.current.user) return;
        setLoading(true);
        await userRepository.current.loadBookmarks();
        // Create new instance to trigger React re-renders
        const updatedUser = UserModel.from(userRepository.current.user);
        setUser(updatedUser);
        setLoading(false);
    }

    async function deleteUser(): Promise<void> {
        try {
            setLoading(true);
            await userRepository.current!.deleteUser();
            await logout();
            setLoading(false);
        } catch (error) {
            console.error('Error deleting user:', error);
            setLoading(false);
            throw error;
        }
    }

    async function changePassword(
        currentPassword: string | null,
        newPassword: string
    ): Promise<void> {
        // Don't call setLoading here - PasswordSettings has its own loading state
        // Calling setLoading causes User context to update, which remounts Profile component
        // and loses the modal state
        await userRepository.current!.changePassword(currentPassword, newPassword);

        // After successful password change, update hasPassword to true
        if (user && !user.hasPassword) {
            user.hasPassword = true;
            // Create a new UserModel instance to trigger React's change detection
            const updatedUser = UserModel.from(user);
            setUser(updatedUser);
        }
    }

    async function uploadAvatar(file: File): Promise<void> {
        // AvatarUpload has its own loading state, so we don't update context loading
        await userRepository.current!.uploadAvatar(file);
        // Reload user data in the repository to get updated avatar URL
        await userRepository.current!.loadUser();
        // Update local user reference without triggering context updates that would close the modal
        if (user && userRepository.current!.user) {
            const updatedUser = UserModel.from(userRepository.current!.user);
            setUser(updatedUser);
        }
    }

    async function deleteAvatar(): Promise<void> {
        // AvatarUpload has its own loading state, so we don't update context loading
        await userRepository.current!.deleteAvatar();
        // Reload user data in the repository to remove avatar
        await userRepository.current!.loadUser();
        // Update local user reference without triggering context updates that would close the modal
        if (user && userRepository.current!.user) {
            const updatedUser = UserModel.from(userRepository.current!.user);
            setUser(updatedUser);
        }
    }

    async function requiresUser({callback}: RequiresUserOptions) {
        if (!user) {
            setAuthDialogIsOpen(true);
            setOnLogin(() => callback);
            return;
        }
        callback();
    }

    const authedBookmarks = userRepository.current?.user?.bookmarkedBalloonEvents || [];
    const bookmarks = user ? authedBookmarks : guestBookmarkEvents;
    const bookmarkedIds = user
        ? authedBookmarks.map((b) => b.id)
        : guestBookmarkIds;

    return (
        <UserContext.Provider
            value={{
                bookmarks,
                bookmarkedIds,
                loadBookmarks,
                userRepositoryRef: userRepository,
                authDialogIsOpen,
                setAuthDialogIsOpen,
                authRedirectAfter,
                setAuthRedirectAfter,
                loading,
                user,
                login,
                loginWithOAuth,
                logout,
                register,
                loadUser,
                toggleBookmark,
                deleteUser,
                changePassword,
                uploadAvatar,
                deleteAvatar,
                googleLogin,
                discordLogin,
                requiresUser,
            }}
        >
            <AuthModal/>
            {children}
        </UserContext.Provider>
    );
};

export function useUser(): UserInfo {
    const context = React.useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}
